import Title from '@cohort/components-xps/components/Title';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import {EmailTransferConfirmation} from '@cohort/wallet/components/space/nftTransfer/EmailTransferConfirmation';
import {EmailTransferDetails} from '@cohort/wallet/components/space/nftTransfer/EmailTransferDetails';
import PolygonTransfer from '@cohort/wallet/components/space/nftTransfer/PolygonTransfer';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {Transition} from '@headlessui/react';
import {EnvelopeSimple} from '@phosphor-icons/react';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

interface TransferWindowProps {
  ownership: OwnershipWDto;
}

type SendingMethod = 'email' | 'polygon';
type EmailsFormStep = 'details' | 'confirmation';

const TransferWindow: React.FC<TransferWindowProps> = ({ownership}) => {
  const [sendingMethod, setSendingMethod] = useState<SendingMethod>('email');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showSendingMethodBtns, setShowSendingMethodBtns] = useState(true);
  const [emailFormStep, setEmailFormStep] = useState<EmailsFormStep>('details');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState('');

  const {t} = useTranslation('components', {keyPrefix: 'space.nftTransfer.transferWindow'});
  const {accentColor, hasDarkBg} = useThemeContext();
  const user = useUserStore(store => store.user);

  const backgroundColorClass = hasDarkBg ? 'bg-white/5' : 'bg-black/5';

  const pageContent = match(emailFormStep)
    .with('details', () => (
      <EmailTransferDetails
        recipientEmail={recipientEmail}
        emailMessage={emailMessage}
        onEmailChange={newEmail => setRecipientEmail(newEmail)}
        onEmailMessageChange={newEmailMessage => setEmailMessage(newEmailMessage)}
        onSubmitDetails={() => {
          setEmailFormStep('confirmation');
          setShowSendingMethodBtns(false);
        }}
      />
    ))
    .with('confirmation', () => (
      <EmailTransferConfirmation
        recipientEmail={recipientEmail}
        emailMessage={emailMessage}
        ownership={ownership}
        onEdit={() => {
          setShowSendingMethodBtns(true);
          setEmailFormStep('details');
        }}
        onTransferSuccess={() => {
          setShowSuccess(true);
        }}
      />
    ))
    .exhaustive();

  const renderSendingForm = useCallback(
    (): JSX.Element => (
      <div className="py-4">
        {sendingMethod === 'email' ? (
          pageContent
        ) : (
          <PolygonTransfer ownership={ownership} onTransferSuccess={() => setShowSuccess(true)} />
        )}
      </div>
    ),
    [sendingMethod, pageContent, ownership]
  );

  if (showSuccess) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-5 px-4 md:flex-row md:justify-start">
        <div>
          <EnvelopeSimple className="h-11 w-11" style={{color: accentColor}} />
        </div>
        <div className="space-y-2 text-center md:text-left">
          <Title content={t('successTitle')} />
          <div>{t('successText', {email: user?.email})}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full max-w-3xl md:flex md:flex-col md:justify-center">
      <h1
        className={classNames(
          'text-center text-3xl font-extrabold md:text-left',
          hasDarkBg ? 'text-white' : 'text-black/90'
        )}
      >
        {t('title')}
      </h1>
      <p
        className={classNames(
          'mt-4 text-center md:text-left',
          hasDarkBg ? 'text-white/50' : 'text-gray-500'
        )}
      >
        {t('subtitle1')}
        <br />
        {t('subtitle2')}
        <br />
        <span className="font-semibold">{t('polygonWarning')}</span>
      </p>

      <div className="mt-4">
        <Transition
          show={showSendingMethodBtns}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <h2 className="mb-1 text-sm">{t('sendingMethodTitle')}</h2>

          <div className="flex flex-col lg:flex-row">
            {/* Send by email btn */}
            <div
              data-testid="send-to-email-address"
              className={classNames(
                'mb-3 cursor-pointer rounded-lg px-6 py-4 text-sm lg:mb-0 lg:mr-2 lg:w-1/2',
                backgroundColorClass,
                sendingMethod === 'email' ? 'border-2' : 'border',
                hasDarkBg ? 'border-white/20 text-white' : 'border-black/20 text-black/90'
              )}
              onClick={() => {
                setSendingMethod('email');
              }}
              style={{...(sendingMethod === 'email' && {borderColor: accentColor})}}
            >
              <h3 className="mb-1 font-medium">{t('sendingEmailTitle')}</h3>
              <p className={hasDarkBg ? 'text-white/50' : 'text-gray-500'}>
                {t('sendingEmailText')}
              </p>
            </div>

            {/* Send to a Polygon wallet btn */}
            <div
              data-testid="send-to-polygon-address"
              className={classNames(
                'cursor-pointer rounded-lg px-6 py-4 text-sm lg:ml-2 lg:w-1/2',
                backgroundColorClass,
                sendingMethod === 'polygon' ? 'border-2' : 'border',
                hasDarkBg ? 'border-white/20 text-white' : 'border-black/20 text-black/90'
              )}
              onClick={() => {
                setSendingMethod('polygon');
              }}
              style={{...(sendingMethod === 'polygon' && {borderColor: accentColor})}}
            >
              <h3 className="mb-1 font-medium">{t('sendingPolygonTitle')}</h3>
              <p className={hasDarkBg ? 'text-white/50' : 'text-gray-500'}>
                {t('sendingPolygonText')}
              </p>
            </div>
          </div>
        </Transition>

        {renderSendingForm()}
      </div>
    </div>
  );
};

export default TransferWindow;
